import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { humanDate, humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import { getApiKeys, deleteApiKey } from "sj-ui-toolkit/src/api/admin/settings";
import { getApiTokensClient } from "sj-ui-toolkit/src/client/svcApiTokens";
import {
  DEFAULT_ROW_COUNT,
  ROWS_PER_PAGE_OPTIONS,
} from "sj-ui-toolkit/src/constants/tableRowsCounts";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type KeyType = "platform" | "aspen" | "reporting";

interface KeyItem {
  id: string;
  name: string;
  type: KeyType;
  typeLabel: string;
  createdAt: string | null;
  lastUsedAt: string | null;
  raw: unknown;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TYPE_OPTIONS = [
  { label: "All Types", value: "all" as const },
  { label: "Platform API Key", value: "platform" as KeyType },
  { label: "Aspen Key", value: "aspen" as KeyType },
  { label: "Reporting API Key", value: "reporting" as KeyType },
];

// ---------------------------------------------------------------------------
// Column body factories
// ---------------------------------------------------------------------------

const createdAtBody = (rowData: KeyItem) => {
  if (!rowData.createdAt) return "--";
  return (
    <span title={humanDate(rowData.createdAt)}>{humanDateWithoutTime(rowData.createdAt)}</span>
  );
};

const lastUsedAtBody = (rowData: KeyItem) => {
  if (!rowData.lastUsedAt) {
    return rowData.type === "reporting" ? "--" : "Never";
  }
  return (
    <span title={humanDate(rowData.lastUsedAt)}>{humanDateWithoutTime(rowData.lastUsedAt)}</span>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const KeyManagement = () => {
  useBreadcrumb({
    label: "Key Management",
    route: AdminAppRoutes.routes.keyManagement.route,
    display: "none",
    resetTrail: true,
  });

  const toast = useRef<Toast>(null);
  const apiTokensClient = getApiTokensClient();

  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<"all" | KeyType>("all");
  const [keys, setKeys] = useState<KeyItem[]>([]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(DEFAULT_ROW_COUNT);

  const showLocalToast = useCallback(
    (
      severity: "success" | "info" | "warn" | "error",
      summary: string,
      detail: string,
      life = 3000
    ) => {
      toast.current?.show({ severity, summary, detail, life });
    },
    []
  );

  const loadKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      const [platformRes, aspenRes, reportingRes] = await Promise.all([
        apiTokensClient.listApiTokens(),
        apiTokensClient.listApiTokensWithEntitlements(["github_action", "guardian"]),
        getApiKeys(),
      ]);

      const aspenUuids = new Set(
        (aspenRes?.data?.apiTokens ?? []).map(t => t.uuid).filter(Boolean)
      );

      const platformKeys: KeyItem[] = (platformRes?.data?.apiTokens ?? [])
        .filter(item => item.uuid && !aspenUuids.has(item.uuid))
        .map(item => ({
          id: item.uuid!,
          name: item.label || item.uuid!,
          type: "platform",
          typeLabel: "Platform API Key",
          createdAt: item.createdAt ?? null,
          lastUsedAt: item.resolvedAt ?? null,
          raw: item,
        }));

      const aspenKeys: KeyItem[] = (aspenRes?.data?.apiTokens ?? [])
        .filter(item => item.uuid)
        .map(item => ({
          id: item.uuid!,
          name: item.label || item.uuid!,
          type: "aspen",
          typeLabel: "Aspen Key",
          createdAt: item.createdAt ?? null,
          lastUsedAt: item.resolvedAt ?? null,
          raw: item,
        }));

      const reportingKeys: KeyItem[] = (reportingRes?.apiKeys ?? []).map(
        (item: { id: number; name: string }) => ({
          id: String(item.id),
          name: item.name,
          type: "reporting",
          typeLabel: "Reporting API Key",
          createdAt: null,
          lastUsedAt: null,
          raw: item,
        })
      );

      setKeys([...platformKeys, ...aspenKeys, ...reportingKeys]);
    } catch (error) {
      console.error("Failed to load keys:", error);
      showLocalToast("error", "Error", "Failed to load keys.", 5000);
    } finally {
      setIsLoading(false);
    }
  }, [showLocalToast, apiTokensClient]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const filteredKeys = useMemo(() => {
    const nameFilter = searchTerm.trim().toLowerCase();

    return keys
      .filter(item => {
        const matchesType = selectedType === "all" || item.type === selectedType;
        const matchesName = !nameFilter || item.name.toLowerCase().includes(nameFilter);
        return matchesType && matchesName;
      })
      .sort((a, b) => {
        const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bDate - aDate;
      });
  }, [keys, searchTerm, selectedType]);

  useEffect(() => {
    setFirst(0);
  }, [searchTerm, selectedType]);

  const confirmDelete = useCallback(
    (key: KeyItem) => {
      confirmDialog({
        message: `Are you sure you want to delete "${key.name}"? This action cannot be undone.`,
        header: "Delete Key",
        icon: "pi pi-exclamation-triangle",
        defaultFocus: "reject",
        acceptClassName: "p-button-danger",
        accept: async () => {
          try {
            if (key.type === "reporting") {
              await deleteApiKey(Number((key.raw as { id: number }).id));
            } else {
              const { error } = await apiTokensClient.revokeApiToken(
                (key.raw as { uuid: string }).uuid
              );
              if (error) throw error;
            }

            setKeys(prev => prev.filter(item => !(item.type === key.type && item.id === key.id)));
            showLocalToast("info", "Deleted", "Key deleted successfully");
          } catch {
            showLocalToast("error", "Error", "Failed to delete key");
          }
        },
      });
    },
    [showLocalToast, apiTokensClient]
  );

  const actionBody = useCallback(
    (rowData: KeyItem) => (
      <Button
        label="Delete"
        severity="danger"
        text
        size="small"
        disabled={isLoading}
        onClick={() => confirmDelete(rowData)}
      />
    ),
    [isLoading, confirmDelete]
  );

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="mb-4">
        <h1>Key Management</h1>
      </div>

      <Card>
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-4">
          <h2 className="atlas-h4">All Keys</h2>
          <div className="flex flex-col md:flex-row gap-2 md:items-center">
            <InputText
              aria-label="Filter keys by name"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Filter by name"
              className="w-full md:w-72"
            />
            <Dropdown
              aria-label="Filter keys by type"
              value={selectedType}
              options={TYPE_OPTIONS}
              onChange={e => setSelectedType(e.value as "all" | KeyType)}
              className="w-full md:w-64"
            />
          </div>
        </div>

        <DataTable
          value={filteredKeys}
          className="p-datatable-sm"
          tableClassName="dt-responsive-text"
          loading={isLoading}
          paginator
          first={first}
          rows={rows}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onPage={e => {
            setFirst(e.first);
            setRows(e.rows);
          }}
          emptyMessage="No keys match the current filters."
        >
          <Column field="name" header="Name" sortable />
          <Column field="typeLabel" header="Type" sortable />
          <Column field="createdAt" header="Created At" body={createdAtBody} sortable />
          <Column field="lastUsedAt" header="Last Used" body={lastUsedAtBody} sortable />
          <Column header="Action" body={actionBody} />
        </DataTable>
      </Card>
    </>
  );
};

export default KeyManagement;
